import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { AuthProvider } from './contexts/AuthContext';
import { FamilyProvider, useFamily } from './contexts/FamilyContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Wishlists from './pages/Wishlists';
import WishlistDetail from './pages/WishlistDetail';
import NamePicker from './pages/NamePicker';
import NamePickerDetail from './pages/NamePickerDetail';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import GiftTracker from './pages/GiftTracker';
import BudgetTracker from './pages/BudgetTracker';
import Calendar from './pages/Calendar';
import Families from './pages/Families';
import FamilyDashboard from './pages/FamilyDashboard';
import AppAdmin from './pages/AppAdmin';
import FamilyFeed from './pages/FamilyFeed';
import Subscription from './pages/Subscription';
import Login from './pages/Login';
import { getHolidayTheme } from './utils/holidayTheme';

// Apply the holiday theme immediately (before React renders) to avoid a flash of the default beige.
(function applyInitialBackground() {
  const ht = getHolidayTheme();
  const defaultPattern = process.env.REACT_APP_DEFAULT_BACKGROUND_IMAGE || '/new-years-background.png';
  const defaultBg = process.env.REACT_APP_THEME_BACKGROUND_COLOR || '#F5E6D3';
  const b = document.body.style;
  b.backgroundSize = 'cover';
  b.backgroundPosition = 'center';
  b.backgroundRepeat = 'no-repeat';
  b.backgroundAttachment = 'fixed';
  if (ht.gradient) {
    b.backgroundImage = ht.gradient;
    b.backgroundColor = ht.background || defaultBg;
  } else {
    b.backgroundImage = `url(${defaultPattern})`;
    b.backgroundColor = defaultBg;
  }
})();

const getTheme = (familyColors = null, holidayColors = null) => {
  // Get default colors from environment variables, with fallbacks
  // Light beige/cream/gold glitter theme: Warm gold accents with neutral browns and dark text
  // Only use family colors if they have a custom background image (explicit customization)
  // Otherwise, use the new theme defaults for everyone automatically
  const useCustomTheme = familyColors && (familyColors.custom_theme || familyColors.background_image_url);
  const hc = holidayColors || {};
  // When the family has a custom theme, use their saved colors as the source;
  // otherwise fall through to the holiday theme, then env vars, then hardcoded defaults.
  const src = useCustomTheme ? familyColors : {};

  const primary       = src.primary_color       || hc.primary   || process.env.REACT_APP_THEME_PRIMARY_COLOR    || '#D4A574';
  const secondary     = src.secondary_color     || hc.secondary || process.env.REACT_APP_THEME_SECONDARY_COLOR  || '#C9A961';
  const background    = src.background_color    || hc.background|| process.env.REACT_APP_THEME_BACKGROUND_COLOR || '#F5E6D3';
  const card          = process.env.REACT_APP_THEME_CARD_COLOR || 'rgba(255, 255, 255, 0.8)';
  const text          = src.text_color          || process.env.REACT_APP_THEME_TEXT_COLOR           || '#5A5047';
  const textSecondary = src.text_secondary_color|| process.env.REACT_APP_THEME_TEXT_SECONDARY_COLOR || '#6B5A4A';
  const buttonText    = src.button_text_color   || process.env.REACT_APP_THEME_BUTTON_TEXT_COLOR    || '#FFFFFF';
  const error         = src.error_color         || process.env.REACT_APP_THEME_ERROR_COLOR          || '#C99A7A';
  const success       = src.success_color       || process.env.REACT_APP_THEME_SUCCESS_COLOR        || '#8B9A8C';
  const warning       = src.warning_color       || process.env.REACT_APP_THEME_WARNING_COLOR        || '#D4B896';
  const info          = src.info_color          || process.env.REACT_APP_THEME_INFO_COLOR           || '#D4A574';
  const appbar        = src.appbar_color        || hc.appbar    || process.env.REACT_APP_FOOTER_COLOR || process.env.REACT_APP_THEME_APPBAR_COLOR || '#9D8B70';
  const link          = src.link_color          || hc.link      || process.env.REACT_APP_THEME_LINK_COLOR        || '#8B7D6B';
  const buttonPrimary   = src.button_primary_color   || process.env.REACT_APP_THEME_BUTTON_PRIMARY_COLOR   || primary;
  const buttonSecondary = src.button_secondary_color || process.env.REACT_APP_THEME_BUTTON_SECONDARY_COLOR || secondary;
  
  // Calculate light/dark variants
  const lightenColor = (color) => {
    // Simple lightening - add 20% white
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const newR = Math.min(255, Math.round(r + (255 - r) * 0.2));
    const newG = Math.min(255, Math.round(g + (255 - g) * 0.2));
    const newB = Math.min(255, Math.round(b + (255 - b) * 0.2));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };
  
  const darkenColor = (color) => {
    // Simple darkening - reduce by 20%
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const newR = Math.max(0, Math.round(r * 0.8));
    const newG = Math.max(0, Math.round(g * 0.8));
    const newB = Math.max(0, Math.round(b * 0.8));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  return createTheme({
    palette: {
      primary: {
        main: buttonPrimary,
        light: lightenColor(buttonPrimary),
        dark: darkenColor(buttonPrimary),
        contrastText: buttonText,
      },
      secondary: {
        main: buttonSecondary,
        light: lightenColor(buttonSecondary),
        dark: darkenColor(buttonSecondary),
        contrastText: buttonText,
      },
      background: {
        default: background,
        paper: card,
      },
      text: {
        primary: text,
        secondary: textSecondary,
      },
      error: {
        main: error,
        light: lightenColor(error),
        dark: darkenColor(error),
        contrastText: buttonText,
      },
      success: {
        main: success,
        light: lightenColor(success),
        dark: darkenColor(success),
        contrastText: buttonText,
      },
      warning: {
        main: warning,
        light: lightenColor(warning),
        dark: darkenColor(warning),
        contrastText: buttonText,
      },
      info: {
        main: info,
        light: lightenColor(info),
        dark: darkenColor(info),
        contrastText: buttonText,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        color: text,
      },
      h2: {
        color: text,
      },
      h3: {
        color: text,
      },
      h4: {
        color: text,
      },
      h5: {
        color: text,
      },
      h6: {
        color: text,
      },
    },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: appbar,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: card,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(184, 149, 106, 0.2)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: link,
          '&:hover': {
            color: darkenColor(link),
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(184, 149, 106, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(184, 149, 106, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});
};

function AppContent() {
  const { currentFamily } = useFamily();
  const holidayTheme = React.useMemo(() => getHolidayTheme(), []);
  const theme = getTheme(currentFamily, holidayTheme);
  
  // Update body background color/image when family changes
  React.useEffect(() => {
    const defaultHolidayPattern = process.env.REACT_APP_DEFAULT_BACKGROUND_IMAGE || '/new-years-background.png';
    const defaultBackgroundColor = process.env.REACT_APP_THEME_BACKGROUND_COLOR || '#F5E6D3';

    if (currentFamily?.background_image_url) {
      // Family has uploaded a custom background — use it
      document.body.style.backgroundImage = `url(${currentFamily.background_image_url})`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundColor = currentFamily.background_color || defaultBackgroundColor;
    } else if (holidayTheme.gradient) {
      // No custom family image — use the auto-detected holiday gradient
      document.body.style.backgroundImage = holidayTheme.gradient;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundColor = holidayTheme.background || defaultBackgroundColor;
    } else {
      // No holiday match — fall back to the default background image from env
      document.body.style.backgroundImage = `url(${defaultHolidayPattern})`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundColor = defaultBackgroundColor;
    }
  }, [currentFamily, holidayTheme]);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Navbar />
          <Box component="main" sx={{ flexGrow: 1 }}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Home />
                  </PrivateRoute>
                }
              />
            <Route
              path="/events"
              element={
                <PrivateRoute>
                  <Events />
                </PrivateRoute>
              }
            />
            <Route
              path="/events/:id"
              element={
                <PrivateRoute>
                  <EventDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/wishlists"
              element={
                <PrivateRoute>
                  <Wishlists />
                </PrivateRoute>
              }
            />
            <Route
              path="/wishlists/:id"
              element={
                <PrivateRoute>
                  <WishlistDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/wishlists/shared/:token"
              element={<WishlistDetail />}
            />
            <Route
              path="/name-picker"
              element={
                <PrivateRoute>
                  <NamePicker />
                </PrivateRoute>
              }
            />
            <Route
              path="/name-picker/:id"
              element={
                <PrivateRoute>
                  <NamePickerDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/name-picker/join/:token"
              element={
                <PrivateRoute>
                  <NamePickerDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/recipes"
              element={
                <PrivateRoute>
                  <Recipes />
                </PrivateRoute>
              }
            />
            <Route
              path="/recipes/:id"
              element={
                <PrivateRoute>
                  <RecipeDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/recipes/shared/:token"
              element={<RecipeDetail />}
            />
            <Route
              path="/gift-tracker"
              element={
                <PrivateRoute>
                  <GiftTracker />
                </PrivateRoute>
              }
            />
            <Route
              path="/budget-tracker"
              element={
                <PrivateRoute>
                  <BudgetTracker />
                </PrivateRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <Calendar />
                </PrivateRoute>
              }
            />
            <Route
              path="/families"
              element={
                <PrivateRoute>
                  <Families />
                </PrivateRoute>
              }
            />
            <Route
              path="/families/join/:token"
              element={
                <PrivateRoute>
                  <Families />
                </PrivateRoute>
              }
            />
            <Route
              path="/families/:id/dashboard"
              element={
                <PrivateRoute>
                  <FamilyDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/families/:id/feed"
              element={
                <PrivateRoute>
                  <FamilyFeed />
                </PrivateRoute>
              }
            />
            <Route
              path="/app-admin"
              element={
                <PrivateRoute>
                  <AppAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <PrivateRoute>
                  <Subscription />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Box>
          {/* Footer Component */}
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <FamilyProvider>
        <AppContent />
      </FamilyProvider>
    </AuthProvider>
  );
}

export default App;
